import { LVGLSchemaService, ElementSchema, CorePosition, CoreTextModel, CoreWordAtPosition } from './';

export type Content = {
    value: string;
    isTrusted: boolean;
    supportHtml: boolean;
    supportThemeIcons: boolean;
};

export type Contents = {
    contents: [
        {
            value: string;
            isTrusted: boolean;
            supportHtml: boolean;
            supportThemeIcons: boolean;
        }
    ];
};

export const provideHover = (
    schemaService: LVGLSchemaService,
    model: CoreTextModel,
    position: CorePosition
): Contents | null => {
    let word: CoreWordAtPosition | null = model.getWordAtPosition(position);
    if (!word) return null;

    // Get text from previous lines until we find the opening element
    let lineNumber: number = position.lineNumber;
    let textUntilElement: string = '';
    let elementFound: boolean = false;

    while (lineNumber > 0 && !elementFound) {
        const lineContent: string = model.getLineContent(lineNumber);
        textUntilElement = lineContent + '\n' + textUntilElement;
        if (lineContent.includes('<')) elementFound = true;
        lineNumber--;
    }

    // Get the element name from the text until the element
    let elementName: string = '';
    const elementNameMatch: RegExpMatchArray | null = textUntilElement.match(/<([a-zA-Z][\w-]*)[^>]*/s);
    if (elementNameMatch) elementName = elementNameMatch[1];
    if (!elementName) return null;

    // Get text from the start of the element to find the complete tag
    let elementStartLine: number = position.lineNumber;
    let elementStartColumn: number = 0;
    let elementEndLine: number = position.lineNumber;
    let elementEndColumn: number = 0;
    let foundStart: boolean = false;
    let foundEnd: boolean = false;

    // Search backwards to find the start of the element
    // Search for the last '<' before the current position in the line, and if not found, go to previous line.
    while (elementStartLine > 0 && !foundStart) {
        const lineContent: string = model.getLineContent(elementStartLine);
        // Find the last '<' in the line before the current position (or anywhere if not the current line)
        let searchColumn: number = lineContent.length;
        if (elementStartLine === position.lineNumber) searchColumn = position.column - 1; // Only consider up to the cursor position in the current line
        const lastLtIndex: number = lineContent.lastIndexOf('<', searchColumn);
        if (lastLtIndex !== -1) {
            elementStartColumn = lastLtIndex;
            foundStart = true;
        } else {
            elementStartLine--;
        }
    }

    // Search forwards to find the end of the element
    while (elementEndLine <= model.getLineCount() && !foundEnd) {
        const lineContent: string = model.getLineContent(elementEndLine);
        const endMatch: RegExpMatchArray | null = lineContent.match(/>/);
        if (endMatch) {
            elementEndColumn = (endMatch.index || 0) + 1;
            foundEnd = true;
        } else {
            elementEndLine++;
        }
    }

    if (!foundStart || !foundEnd) return null;

    // Get the complete element tag content
    let elementTag: string = '';
    for (let line = elementStartLine; line <= elementEndLine; line++) {
        const lineContent: string = model.getLineContent(line);
        if (line === elementStartLine) elementTag += lineContent.substring(elementStartColumn);
        else if (line === elementEndLine) elementTag += lineContent.substring(0, elementEndColumn);
        else elementTag += lineContent;

        if (line !== elementEndLine) elementTag += '\n';
    }

    let extendsValue: string = '';
    const extendsMatch: RegExpMatchArray | null = elementTag.match(/extends=["']([^"']*)["']/);
    if (extendsMatch) extendsValue = extendsMatch[1];

    // When hovering over an element name, show the properties of the element
    if (elementNameMatch && word.word === elementName) {
        const element: ElementSchema = schemaService.elements.get(elementName) as ElementSchema; // Display the allowed children of the element

        return {
            contents: [
                {
                    value: element?.help ? `#### ${elementName} element<br>${element.help}` : '',
                    isTrusted: true,
                    supportHtml: true,
                    supportThemeIcons: true
                }
            ]
        };
    }

    const [propType, propElement] = schemaService.getPropertyType(elementName, word.word, extendsValue);

    if (propType) {
        let content: string = `#### ${word.word} property of ${propElement}`;

        content += typeof propType === 'string' ? `\n\n**Type:** _${propType}_` : '';
        // If 'propType' is a string and starts with enum:, add the allowed values
        if (typeof propType === 'string' && (propType.startsWith('enum:') || propType === 'lv_event')) {
            const enumType: string = propType.startsWith('enum:') ? propType.split(':')[1] : propType;
            const isMultiValue: boolean = enumType.endsWith('+');
            const baseEnumType: string = isMultiValue ? enumType.slice(0, -1) : enumType;
            const values: string[] = schemaService.getEnumValues(baseEnumType);

            content += `\n\n**Allowed values:**<br>${values.map((v) => `${v}`).join('<br>')}`;

            if (isMultiValue)
                content += `\n\n*Multiple values can be combined using the | character*<br>Example: \`value1|value2|value3\``;
        } else if (propType === 'event_cb') {
            content += '\n\n**Type:** _event callback function name_';
        } else if (propType === 'screen') {
            content += '\n\n**Type:** _screen reference_ (path-like name, e.g. `main_menu` or `folder/screen1`)';
        }

        // Add the help text of the property
        const help: string | undefined = schemaService.getPropertyHelp(elementName, word.word);
        if (help) content += `\n\n${help}`;

        return {
            contents: [
                {
                    value: content,
                    isTrusted: true,
                    supportHtml: true,
                    supportThemeIcons: true
                }
            ]
        };
    }

    return null;
};
